<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-medium"
    :title="userData ? 'ユーザ情報変更' : 'ユーザ追加'"
  >
    <template v-if="isLoaded" v-slot:body="{ cancel }">
      <validation-observer
        v-slot="{ handleSubmit }"
        :initial-values="{
          ipAuthAddressList:
            form.ipAuthAddressList && form.ipAuthAddressList.length > 0
              ? form.ipAuthAddressList
              : [''],
        }"
        as="div"
      >
        <v-form novalidate @submit.stop.prevent="handleSubmit(submit)">
          <!-- BODY -->
          <v-card-text class="pa-0">
            <div class="modal-body">
              <v-container>
                <!--            最終更新日-->
                <v-row v-if="userData">
                  <v-col>
                    <div class="text-right">
                      最終更新日：{{ $filter.date(userData.updateDate) }}
                    </div>
                  </v-col>
                </v-row>
                <!--            ログインID-->
                <v-row>
                  <v-col>
                    <app-badge
                      item="ログインID"
                      msg="先頭2桁は大文字小文字混在のアルファベット+半角数字5桁
                      または先頭1桁は大文字、小文字のアルファベット+半角数字6桁"
                      :isRequired="!userData"
                      :isInfo="!userData"
                    />
                    <template v-if="userData">
                      <span class="ml-3">{{ userData.loginId }}</span>
                    </template>
                    <template v-else>
                      <validation-provider
                        name="loginId"
                        v-model="form.loginId"
                        label="ログインID"
                        :rules="{ required: true, login_id: true }"
                        v-slot="{ componentField, errorMessage, meta }"
                      >
                        <app-form-input
                          v-bind="componentField"
                          :error-messages="errorMessage"
                          :field-meta="meta"
                          :suffix="`@${vnumber}`"
                        />
                      </validation-provider>
                    </template>
                  </v-col>
                </v-row>
                <!--            姓-->
                <v-row>
                  <v-col>
                    <app-badge item="姓" msg="10文字以内" :isRequired="true" />
                    <validation-provider
                      name="familyName"
                      v-model="form.familyName"
                      label="姓"
                      :rules="{ required: true, max: { length: 10 } }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input
                        v-bind="componentField"
                        :field-meta="meta"
                        :error-messages="errorMessage"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>
                <!--            名-->
                <v-row>
                  <v-col>
                    <app-badge item="名" msg="10文字以内" :isRequired="true" />
                    <validation-provider
                      name="givenName"
                      v-model="form.givenName"
                      label="名"
                      :rules="{ required: true, max: { length: 10 } }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input
                        v-bind="componentField"
                        :error-messages="errorMessage"
                        :field-meta="meta"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>
                <!--            姓（カナ）-->
                <v-row>
                  <v-col>
                    <app-badge
                      item="姓(カナ)"
                      msg="全角カナ20文字以内"
                      :isRequired="false"
                    />
                    <validation-provider
                      name="familyNameKana"
                      v-model="form.familyNameKana"
                      label="姓(カナ)"
                      :rules="{ is_kana_zenkaku: true, max: { length: 20 } }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input
                        v-bind="componentField"
                        :error-messages="errorMessage"
                        :field-meta="meta"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>
                <!--            名（カナ）-->
                <v-row>
                  <v-col>
                    <app-badge
                      item="名(カナ)"
                      msg="全角カナ20文字以内"
                      :isRequired="false"
                    />
                    <validation-provider
                      name="givenNameKana"
                      v-model="form.givenNameKana"
                      label="名(カナ)"
                      :rules="{ is_kana_zenkaku: true, max: { length: 20 } }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input
                        v-bind="componentField"
                        :error-messages="errorMessage"
                        :field-meta="meta"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>
                <!--            IPアドレス認証-->
                <v-row>
                  <v-col>
                    <app-badge
                      item="IPアドレス認証"
                      :isInfo="isAuthAddress"
                      :isRequired="isAuthAddress"
                      :msg="$filter.message('info_authentication_ip_address')"
                    />
                    <v-radio-group inline v-model="isAuthAddress" hide-details>
                      <v-radio label="無" :value="false" />
                      <v-radio label="有" :value="true" />
                    </v-radio-group>
                    <template v-if="isAuthAddress">
                      <v-label
                        text="認証IPアドレス"
                        class="text-caption mb-1"
                      />
                      <validation-array
                        name="ipAuthAddressList"
                        v-slot="{ fields, push, remove }"
                      >
                        <template
                          v-for="(item, index) in fields"
                          :key="item.key"
                        >
                          <v-row class="ga-2">
                            <validation-provider
                              :name="`authIP_${item.key}`"
                              v-model="form.ipAuthAddressList[index]"
                              label="認証IPアドレス"
                              :rules="{
                                required: true,
                                is_ipv4_or_subnet: {
                                  min: 1,
                                  max: 32,
                                  exclude: [31],
                                },
                                is_not_loopback_ip: true,
                                is_not_multicast_ip: true,
                                is_not_class_e_ip: true,
                                is_not_broadcast_ip: true,
                                is_not_network_ip: true,
                                duplicate: { list: ipAddressList(index) },
                              }"
                              v-slot="{ componentField, errorMessage, meta }"
                              :keepValue="true"
                            >
                              <app-form-input
                                :placeholder="
                                  $filter.message(
                                    'placeholder_ip_address_with_subnet24_or_non_subnet'
                                  )
                                "
                                v-bind="componentField"
                                :error-messages="errorMessage"
                                :field-meta="meta"
                              />
                            </validation-provider>

                            <v-btn
                              icon="$mdiMinus"
                              size="small"
                              variant="outlined"
                              class="rounded-sm"
                              @click="removeIpAddress(index, remove)"
                            />
                          </v-row>
                        </template>
                        <v-row justify="end">
                          <v-btn
                            :disabled="form.ipAuthAddressList.length >= 10"
                            icon="$mdiPlus"
                            size="small"
                            variant="outlined"
                            class="rounded-sm"
                            @click="addIpAddress(push)"
                          />
                        </v-row>
                      </validation-array>
                    </template>
                  </v-col>
                </v-row>
                <!--            所属グループ-->
                <v-row>
                  <v-col>
                    <app-badge
                      item="所属グループ"
                      :isRequired="groupType !== 'NONE'"
                      :isInfo="groupType === 'NEW'"
                      :msg="
                        $filter.message('info_max_length', { maxLength: 50 })
                      "
                    />
                    <v-radio-group inline v-model="groupType">
                      <v-radio label="無" value="NONE" />
                      <v-radio label="既存グループ" value="EXISTS" />
                      <v-radio label="新規グループ" value="NEW" />
                    </v-radio-group>
                    <template v-if="groupType === 'EXISTS'">
                      <validation-provider
                        name="userGroupListSelect"
                        v-model="userGroupListSelect"
                        label="既存グループ"
                        :rules="{ required: true }"
                        v-slot="{ componentField, errorMessage, meta }"
                      >
                        <app-select-search
                          :items="userGroupList"
                          v-bind="componentField"
                          :error-messages="errorMessage"
                          :field-meta="meta"
                        />
                      </validation-provider>
                    </template>
                    <template v-else-if="groupType === 'NEW'">
                      <validation-provider
                        name="newGroup"
                        v-model="newGroup"
                        label="新規グループ"
                        :rules="{ required: true, max: { length: 50 } }"
                        v-slot="{ componentField, errorMessage, meta }"
                      >
                        <app-form-input
                          v-bind="componentField"
                          :error-messages="errorMessage"
                          :field-meta="meta"
                        />
                      </validation-provider>
                    </template>
                  </v-col>
                </v-row>
                <!--            連絡先電話番号-->
                <v-row>
                  <v-col>
                    <app-badge
                      item="連絡先電話番号"
                      :isRequired="false"
                      msg="半角数字15桁以内(-なし)"
                    />
                    <validation-provider
                      name="phone"
                      v-model="form.phone"
                      label="連絡先電話番号"
                      :rules="{ is_phone_number: { max: 15 } }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input
                        v-bind="componentField"
                        :error-messages="errorMessage"
                        :field-meta="meta"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>
                <!--            連絡先メールアドレス-->
                <v-row>
                  <v-col>
                    <app-badge
                      item="連絡先メールアドレス"
                      :isRequired="true"
                      :msg="$filter.message('info_mail')"
                    />
                    <validation-provider
                      name="email"
                      v-model="form.email"
                      label="連絡先メールアドレス"
                      :rules="{
                        required: true,
                        max: { length: 127 },
                        is_email: true,
                      }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input
                        v-bind="componentField"
                        :error-messages="errorMessage"
                        :field-meta="meta"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>
                <!--            運用種別-->
                <v-row>
                  <v-col>
                    <app-badge
                      item="運用種別"
                      :isInfo="false"
                      :isRequired="false"
                    />
                    <v-radio-group inline v-model="form.operationType">
                      <v-radio
                        label="法人管理者"
                        value="ADMIN"
                        v-if="showAdminType"
                      />
                      <v-radio
                        label="設定権限"
                        value="SETTING"
                        v-if="showSettingType"
                      />
                      <v-radio label="閲覧権限" value="VIEWING" />
                    </v-radio-group>
                  </v-col>
                </v-row>
              </v-container>
            </div>
          </v-card-text>

          <v-divider></v-divider>
          <!-- FOOTER -->
          <v-card-actions>
            <v-list-item class="w-100 pa-1">
              <template v-slot:append>
                <v-btn type="submit" color="primary" variant="flat">{{
                  userData ? "変更" : "追加"
                }}</v-btn>
                <v-btn @click="cancel" variant="outlined" color="primary"
                  >キャンセル</v-btn
                >
              </template>
            </v-list-item>
          </v-card-actions>
        </v-form>
      </validation-observer>
    </template>
  </app-modal>
</template>

<script lang="ts">
import type { PropType } from "vue";
import { defineComponent } from "vue";
import type { UserEntity } from "@/apis/UserApi";
import cloneDeep from "lodash/cloneDeep";
import { VueFinalModal } from "vue-final-modal";

export default defineComponent({
  name: "UserEdit",
  props: {
    vnumber: {
      type: String as PropType<string>,
      required: false,
    },
    userData: {
      type: Object as PropType<UserEntity>,
      required: false,
    },
    userKeyId: {
      type: String as PropType<string>,
      required: false,
    },
  },
  data() {
    return {
      isLoaded: false,
      form:
        cloneDeep(this.userData) ??
        ({
          ipAuthAddressList: [""],
          operationType: this.$service.permission.hasAuthority("ADMIN")
            ? "ADMIN"
            : this.$service.permission.hasAuthority("CONFIG")
              ? "SETTING"
              : "VIEWING",
        } as UserEntity),
      isAuthAddress: (this.userData?.ipAuthAddressList.length ?? 0) > 0,
      groupType: (this.userData?.group ? "EXISTS" : "NONE") as
        | "NONE"
        | "EXISTS"
        | "NEW",
      newGroup: null,
      userGroupListSelect: this.userData?.group ?? null,
      userGroupList: [] as string[],
    };
  },
  watch: {
    isAuthAddress: function () {
      if (this.form.ipAuthAddressList.length < 1) {
        this.form.ipAuthAddressList = [""];
      }
    },
  },
  computed: {
    /** 認証IPアドレスの重複チェック用リスト作成 */
    ipAddressList() {
      return (index: number) => {
        return this.form.ipAuthAddressList.filter((_, num) => num !== index);
      };
    },
    /**
     * true: 運用種別で法人管理者を選択可能, false: 選択不可
     * 法人の場合は自身の権限以下の運用種別が選択可能
     * 代行の場合は全てが選択可能、閲覧代行はこの画面を表示できないので考慮不要
     */
    showAdminType(): boolean {
      return (
        this.$service.permission.isDelegateUser() ||
        this.$service.permission.hasAuthority("ADMIN")
      );
    },
    /**
     * true: 運用種別で設定権限を選択可能, false: 選択不可
     * 法人の場合は自身の権限以下の運用種別が選択可能
     * 代行の場合は全てが選択可能、閲覧代行はこの画面を表示できないので考慮不要
     */
    showSettingType(): boolean {
      return (
        this.$service.permission.isDelegateUser() ||
        this.$service.permission.hasAuthority("CONFIG")
      );
    },
  },
  async mounted() {
    this.userGroupList = (await this.$api.user.getGroupList()).userGroupList;
    // 認証IPアドレスを数値順でソート
    if (this.form.ipAuthAddressList && this.form.ipAuthAddressList.length > 0) {
      this.form.ipAuthAddressList = this.form.ipAuthAddressList.sortIpAddress();
    }
    this.isLoaded = true;
  },
  methods: {
    /** 認証IPアドレス追加 */
    addIpAddress(push: (item: any) => void) {
      const addItem = "";
      this.form.ipAuthAddressList = [...this.form.ipAuthAddressList, addItem];
      // ValidationにFieldを追加することを伝える
      push(addItem);
    },
    /** 認証IPアドレス削除 */
    removeIpAddress(index: number, remove: (idx: number) => void) {
      if (this.form.ipAuthAddressList.length > 1) {
        this.form.ipAuthAddressList.splice(index, 1);
        // ValidationにFieldを削除することを伝える
        remove(index);
      } else {
        this.form.ipAuthAddressList = [""];
      }
    },
    async submit() {
      this.form.group =
        this.groupType === "NONE"
          ? null
          : this.groupType === "EXISTS"
            ? this.userGroupListSelect
            : this.newGroup;
      if (this.userData) {
        await this.$confirm("ユーザ情報を変更します。よろしいですか？");
        await this.$api.user.update(this.form.userSeq, {
          ...this.form,
          ipAuthAddressList: this.isAuthAddress
            ? this.form.ipAuthAddressList.length === 1 &&
              !this.form.ipAuthAddressList[0]
              ? []
              : this.form.ipAuthAddressList
            : [],
          userKeyId: this.userKeyId!,
        });
      } else {
        await this.$confirm("ユーザを追加します。よろしいですか？");
        await this.$api.user.post({
          ...this.form,
          ipAuthAddressList: this.isAuthAddress
            ? this.form.ipAuthAddressList
            : [],
          oneTimeUrl: `${window.location.origin}/password/reset`,
        });
      }
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },
  },
});
</script>
